import React, { useEffect, useMemo, useState } from "react";
/*
  Drop-in page: src/pages/nfl/NflTeamDataDashboard.jsx
  IMPORTANT CHANGE:
  - Uses SIM / PROJECTION files as the primary source for all projected stats.
  - Uses positional data files only as context for salary, opponent, time, O/U, spread, etc. Projected stat columns are locked to player_sim_results.json.
*/
const POS_ORDER = ["QB", "RB", "WR", "TE", "DST", "K"];
const BASE_PROJ = "/data/nfl/latest";
const PROJECTION_SOURCES = {
  player: `${BASE_PROJ}/player_sim_results.json`,
  team: `${BASE_PROJ}/team_sim_results.json`,
  game: `${BASE_PROJ}/game_sim_results.json`,
};
const CLASSIC_BASE = "/data/nfl/classic/latest";
const CONTEXT_SOURCES = {
  QB: `${CLASSIC_BASE}/qb_data.json`,
  RB: `${CLASSIC_BASE}/rb_data.json`,
  WR: `${CLASSIC_BASE}/wr_data.json`,
  TE: `${CLASSIC_BASE}/te_data.json`,
  DST: `${CLASSIC_BASE}/dst_data.json`,
  K: `${CLASSIC_BASE}/k_data.json`,
};
const META_URL = `${CLASSIC_BASE}/meta.json`;
const LOGO_BASE = "/logos/nfl";
const LOGO_EXT = "png";
const TEAM_ABBR_MAP = {
  ARZ: "ARI",
  WSH: "WAS",
  JAC: "JAX",
  OAK: "LV",
  SD: "LAC",
  STL: "LAR",
};
const TEAM_META = {
  ARI: { name: "Arizona Cardinals", primary: "#97233F", secondary: "#FFB612" },
  ATL: { name: "Atlanta Falcons", primary: "#A71930", secondary: "#000000" },
  BAL: { name: "Baltimore Ravens", primary: "#241773", secondary: "#9E7C0C" },
  BUF: { name: "Buffalo Bills", primary: "#00338D", secondary: "#C60C30" },
  CAR: { name: "Carolina Panthers", primary: "#0085CA", secondary: "#101820" },
  CHI: { name: "Chicago Bears", primary: "#0B162A", secondary: "#C83803" },
  CIN: { name: "Cincinnati Bengals", primary: "#FB4F14", secondary: "#000000" },
  CLE: { name: "Cleveland Browns", primary: "#311D00", secondary: "#FF3C00" },
  DAL: { name: "Dallas Cowboys", primary: "#003594", secondary: "#869397" },
  DEN: { name: "Denver Broncos", primary: "#FB4F14", secondary: "#002244" },
  DET: { name: "Detroit Lions", primary: "#0076B6", secondary: "#B0B7BC" },
  GB: { name: "Green Bay Packers", primary: "#203731", secondary: "#FFB612" },
  HOU: { name: "Houston Texans", primary: "#03202F", secondary: "#A71930" },
  IND: { name: "Indianapolis Colts", primary: "#002C5F", secondary: "#A2AAAD" },
  JAX: { name: "Jacksonville Jaguars", primary: "#006778", secondary: "#D7A22A" },
  KC: { name: "Kansas City Chiefs", primary: "#E31837", secondary: "#FFB81C" },
  LV: { name: "Las Vegas Raiders", primary: "#000000", secondary: "#A5ACAF" },
  LAC: { name: "Los Angeles Chargers", primary: "#0080C6", secondary: "#FFC20E" },
  LAR: { name: "Los Angeles Rams", primary: "#003594", secondary: "#FFA300" },
  MIA: { name: "Miami Dolphins", primary: "#008E97", secondary: "#FC4C02" },
  MIN: { name: "Minnesota Vikings", primary: "#4F2683", secondary: "#FFC62F" },
  NE: { name: "New England Patriots", primary: "#002244", secondary: "#C60C30" },
  NO: { name: "New Orleans Saints", primary: "#D3BC8D", secondary: "#101820" },
  NYG: { name: "New York Giants", primary: "#0B2265", secondary: "#A71930" },
  NYJ: { name: "New York Jets", primary: "#125740", secondary: "#000000" },
  PHI: { name: "Philadelphia Eagles", primary: "#004C54", secondary: "#A5ACAF" },
  PIT: { name: "Pittsburgh Steelers", primary: "#FFB612", secondary: "#101820" },
  SEA: { name: "Seattle Seahawks", primary: "#002244", secondary: "#69BE28" },
  SF: { name: "San Francisco 49ers", primary: "#AA0000", secondary: "#B3995D" },
  TB: { name: "Tampa Bay Buccaneers", primary: "#D50A0A", secondary: "#FF7900" },
  TEN: { name: "Tennessee Titans", primary: "#0C2340", secondary: "#4B92DB" },
  WAS: { name: "Washington Commanders", primary: "#5A1414", secondary: "#FFB612" },
};
const LIGHT_THEME = {
  page: "min-h-screen bg-slate-100 text-slate-900",
  card: "border border-slate-200 bg-white shadow-sm",
  headerBg: "bg-slate-50",
  zebraOdd: "bg-white",
  zebraEven: "bg-slate-50",
  border: "border-slate-200",
  text: "text-slate-900",
  subtext: "text-slate-600",
  input: "bg-white border-slate-300 text-slate-900 placeholder:text-slate-400",
};
const DARK_THEME = {
  page: "min-h-screen bg-slate-950 text-slate-100",
  card: "border border-slate-800 bg-slate-900/95 shadow-sm",
  headerBg: "bg-slate-900",
  zebraOdd: "bg-slate-950",
  zebraEven: "bg-slate-900",
  border: "border-slate-800/80",
  text: "text-slate-100",
  subtext: "text-slate-400",
  input: "bg-slate-950/80 border-slate-700 text-slate-100 placeholder:text-slate-500",
};
const norm = (s) => String(s ?? "").trim();
const lower = (s) => norm(s).toLowerCase();
const fixAbbr = (code) => TEAM_ABBR_MAP[String(code || "").toUpperCase()] || String(code || "").toUpperCase();
const keynorm = (s) =>
  lower(String(s))
    .replace(/[%]/g, "pct")
    .replace(/[\s._/-]/g, "")
    .replace(/[()]/g, "");
const nameKey = (s) =>
  lower(s)
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[.'’,’-]/g, " ")
    .replace(/\s+(jr|sr|ii|iii|iv)\b/g, "")
    .replace(/\s+/g, " ")
    .trim();
function parseNumericLike(v) {
  if (v == null) return null;
  const s = String(v).trim();
  if (s === "") return null;
  if (/%$/.test(s)) {
    const n = Number(s.replace(/%/g, ""));
    return Number.isFinite(n) ? n : null;
  }
  const n = Number(s.replace(/[$,%\s]/g, ""));
  return Number.isFinite(n) ? n : null;
}
function fmtTime(s) {
  const raw = norm(s);
  if (!raw) return "";
  if (/\b(am|pm)\b/i.test(raw)) return raw.replace(/\s+/g, "").toUpperCase().replace("AM", " AM").replace("PM", " PM");
  const m = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (!m) return raw;
  let hh = Number(m[1]);
  const mm = m[2];
  const ampm = hh >= 12 ? "PM" : "AM";
  hh = ((hh + 11) % 12) + 1;
  return `${hh}:${mm} ${ampm}`;
}
function fmtCellValue(label, val) {
  const v = norm(val);
  if (!v) return "";
  if (/^time$/i.test(label)) return fmtTime(v);
  if (/%$/.test(v)) return v;
  const isRateCol = /%/.test(label) || /^tgt\s*shr$/i.test(label) || /^target\s*share$/i.test(label) || /^msair$/i.test(label);
  const n = parseNumericLike(v);
  if (n == null) return v;
  if (/\b(sal|salary)\b/i.test(label)) return Math.round(n).toLocaleString();
  if (isRateCol) {
    const pct = n <= 1.5 ? n * 100 : n;
    const out = pct.toFixed(1);
    return `${out.endsWith(".0") ? out.slice(0, -2) : out}%`;
  }
  const out = n.toFixed(2);
  if (out.endsWith(".00")) return out.slice(0, -3);
  if (out.endsWith("0")) return out.slice(0, -1);
  return out;
}
async function fetchJsonLoose(url) {
  const r = await fetch(`${url}?_=${Date.now()}`, { cache: "no-store" });
  if (!r.ok) throw new Error(`HTTP ${r.status} @ ${url}`);
  const txt = await r.text();
  const looksJson = /^[\s]*[**\\[**{]/.test(txt);
  if (!looksJson) throw new Error(`Not JSON @ ${url}`);
  const j = JSON.parse(txt);
  return Array.isArray(j) ? j : j?.rows || j?.data || [];
}
function useJson(url, optional = false) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const arr = await fetchJsonLoose(url);
        if (alive) setData(arr);
      } catch (e) {
        if (alive) {
          setData([]);
          setErr(optional ? "" : String(e?.message || e));
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [url, optional]);
  return { data, loading, err };
}
function useMeta(url) {
  const [meta, setMeta] = useState(null);
  useEffect(() => {
    let ok = true;
    (async () => {
      try {
        const r = await fetch(`${url}?_=${Date.now()}`, { cache: "no-store" });
        if (!r.ok) throw new Error();
        const j = await r.json();
        if (ok) setMeta(j);
      } catch {
        if (ok) setMeta(null);
      }
    })();
    return () => { ok = false; };
  }, [url]);
  return meta;
}
function unionKeys(rows) {
  const ordered = [];
  const seen = new Set();
  for (const row of rows || []) {
    for (const k of Object.keys(row || {})) {
      if (!seen.has(k)) {
        seen.add(k);
        ordered.push(k);
      }
    }
  }
  return ordered;
}
function resolveKey(rawCols, aliases) {
  const map = new Map(rawCols.map((k) => [keynorm(k), k]));
  for (const a of aliases || []) {
    const hit = map.get(keynorm(a));
    if (hit) return hit;
  }
  for (const a of aliases || []) {
    const want = keynorm(a);
    const hit = rawCols.find((k) => keynorm(k).includes(want) || want.includes(keynorm(k)));
    if (hit) return hit;
  }
  return null;
}
function getFirst(row, aliases) {
  const rawCols = Object.keys(row || {});
  const k = resolveKey(rawCols, aliases);
  return k ? row[k] : "";
}
function getFirstMerged(merged, aliases) {
  const pVal = getFirst(merged?.proj, aliases);
  if (pVal !== "" && pVal != null) return pVal;
  const cVal = getFirst(merged?.ctx, aliases);
  if (cVal !== "" && cVal != null) return cVal;
  return "";
}
function getTeam(row) {
  return fixAbbr(getFirst(row, ["Team", "team", "Tm", "tm", "TEAM", "TeamAbbrev", "teamabbrev"]));
}
function getSlot(row) {
  const raw = norm(getFirst(row, ["Slot", "slot", "POS", "Pos", "Position", "position"])).toUpperCase();
  if (!raw) return "";
  if (raw.includes("DST") || raw.includes("DEF")) return "DST";
  if (raw === "PK") return "K";
  return raw.replace(/[^A-Z]/g, "");
}
function getPlayer(row, fallbackPos = "") {
  const player = norm(getFirst(row, ["Player", "player", "Name", "name", "Player Name", fallbackPos, `${fallbackPos}1`, "DST", "Defense", "Kicker"]));
  if (player) return player;
  const team = getTeam(row);
  const slot = getSlot(row) || fallbackPos;
  if (slot === "DST" && team) return `${team} DST`;
  return "";
}
function getMergedPlayer(m, pos) {
  return getPlayer(m?.proj, pos) || getPlayer(m?.ctx, pos);
}
function rowIdentity(row, pos) {
  const player = getPlayer(row, pos);
  const team = getTeam(row);
  return `${team}|${pos}|${nameKey(player)}`;
}
function looseIdentity(row) {
  const player = getPlayer(row);
  const team = getTeam(row);
  return `${team}|${nameKey(player)}`;
}
function TeamWithLogo({ code, className = "h-6 w-6" }) {
  const c = fixAbbr(code);
  if (!c) return null;
  return (
    <span className="inline-flex items-center gap-2">
      <img
        src={`${LOGO_BASE}/${c}.${LOGO_EXT}`}
        alt=""
        className={`${className} shrink-0 object-contain`}
        onError={(e) => (e.currentTarget.style.display = "none")}
      />
      <span>{c}</span>
    </span>
  );
}
const CONTEXT_ALIASES = {
  dkSal: ["DK Sal", "DK Salary", "dk_sal", "dk salary"],
  fdSal: ["FD Sal", "FD Salary", "fd_sal", "fd salary"],
  opp: ["Opp", "OPP", "opp", "Opponent"],
  time: ["Time", "time", "Kickoff"],
  total: ["O/U", "OU", "Total", "Game Total"],
  imp: ["Imp Total", "Implied Total", "ImpTot", "Team Total"],
  spread: ["Spread", "Line", "Spr"],
};
const DKFD_COLS = [
  { label: "DK Mean", aliases: ["DK_Mean", "DK Mean", "DK Proj", "dk_proj", "DKPts", "DK Pts"], sourceOnly: "proj" },
  { label: "DK Floor", aliases: ["DK_P10", "DK Floor"], sourceOnly: "proj" },
  { label: "DK Ceiling", aliases: ["DK_P90", "DK Ceiling"], sourceOnly: "proj" },
  { label: "FD Mean", aliases: ["FD_Mean", "FD Mean", "FD Proj", "fd_proj", "FDPts", "FD Pts"], sourceOnly: "proj" },
  { label: "FD Floor", aliases: ["FD_P10", "FD Floor"], sourceOnly: "proj" },
  { label: "FD Ceiling", aliases: ["FD_P90", "FD Ceiling"], sourceOnly: "proj" },
];
const BASE_PLAYER_COLS = [
  { label: "Player", aliases: ["Player", "player", "Name", "Player Name"] },
  { label: "DK Sal", aliases: CONTEXT_ALIASES.dkSal, sourceOnly: "ctx" },
  { label: "FD Sal", aliases: CONTEXT_ALIASES.fdSal, sourceOnly: "ctx" },
  { label: "OPP", aliases: CONTEXT_ALIASES.opp, sourceOnly: "ctx" },
];
const COLUMN_SETS = {
  QB: [
    ...BASE_PLAYER_COLS,
    { label: "Time", aliases: CONTEXT_ALIASES.time, sourceOnly: "ctx" },
    { label: "O/U", aliases: CONTEXT_ALIASES.total, sourceOnly: "ctx" },
    { label: "Imp Total", aliases: CONTEXT_ALIASES.imp, sourceOnly: "ctx" },
    { label: "Spread", aliases: CONTEXT_ALIASES.spread, sourceOnly: "ctx" },
    ...DKFD_COLS,
    { label: "Pass Att", aliases: ["PassAtt_Mean", "PassAtt", "Pass Att", "Pass Attempts"], sourceOnly: "proj" },
    { label: "Pass Yds", aliases: ["PassYd_Mean", "PassYds_Mean", "PassYd", "PassYds", "Pass Yds", "Pass Yards"], sourceOnly: "proj" },
    { label: "Pass TD", aliases: ["PassTD_Mean", "PassTD", "Pass TD"], sourceOnly: "proj" },
    { label: "INT", aliases: ["Int_Mean", "INT_Mean", "Int", "INT"], sourceOnly: "proj" },
    { label: "Rush Att", aliases: ["RushAtt_Mean", "RushAtt", "Rush Att"], sourceOnly: "proj" },
    { label: "Rush Yds", aliases: ["RushYd_Mean", "RushYds_Mean", "RushYd", "RushYds", "Rush Yds"], sourceOnly: "proj" },
    { label: "Rush TD", aliases: ["RushTD_Mean", "RushTD", "Rush TD"], sourceOnly: "proj" },
  ],
  RB: [
    ...BASE_PLAYER_COLS,
    ...DKFD_COLS,
    { label: "Rush Att", aliases: ["RushAtt_Mean", "RushAtt", "Rush Att", "Carries"], sourceOnly: "proj" },
    { label: "Rush Yds", aliases: ["RushYd_Mean", "RushYds_Mean", "RushYd", "RushYds", "Rush Yds"], sourceOnly: "proj" },
    { label: "Rush TD", aliases: ["RushTD_Mean", "RushTD", "Rush TD"], sourceOnly: "proj" },
    { label: "Targets", aliases: ["Targets_Mean", "Targets", "Tgts", "Tgt"], sourceOnly: "proj" },
    { label: "Target Share", aliases: ["TargetShare_Mean", "TargetShare", "TargetShare_Pct", "Tgt Shr", "Target Share"], sourceOnly: "proj" },
    { label: "Receptions", aliases: ["Receptions_Mean", "Receptions", "Rec", "Catches"], sourceOnly: "proj" },
    { label: "Rec Yds", aliases: ["RecYd_Mean", "RecYds_Mean", "RecYd", "RecYds", "Rec Yds"], sourceOnly: "proj" },
    { label: "Rec TD", aliases: ["RecTD_Mean", "RecTD", "Rec TD"], sourceOnly: "proj" },
  ],
  WR: [
    ...BASE_PLAYER_COLS,
    ...DKFD_COLS,
    { label: "Targets", aliases: ["Targets_Mean", "Targets", "Tgts", "Tgt"], sourceOnly: "proj" },
    { label: "Target Share", aliases: ["TargetShare_Mean", "TargetShare", "TargetShare_Pct", "Tgt Shr", "Target Share"], sourceOnly: "proj" },
    { label: "Receptions", aliases: ["Receptions_Mean", "Receptions", "Rec", "Catches"], sourceOnly: "proj" },
    { label: "Rec Yds", aliases: ["RecYd_Mean", "RecYds_Mean", "RecYd", "RecYds", "Rec Yds"], sourceOnly: "proj" },
    { label: "Rec TD", aliases: ["RecTD_Mean", "RecTD", "Rec TD"], sourceOnly: "proj" },
  ],
  TE: [
    ...BASE_PLAYER_COLS,
    ...DKFD_COLS,
    { label: "Targets", aliases: ["Targets_Mean", "Targets", "Tgts", "Tgt"], sourceOnly: "proj" },
    { label: "Target Share", aliases: ["TargetShare_Mean", "TargetShare", "TargetShare_Pct", "Tgt Shr", "Target Share"], sourceOnly: "proj" },
    { label: "Receptions", aliases: ["Receptions_Mean", "Receptions", "Rec", "Catches"], sourceOnly: "proj" },
    { label: "Rec Yds", aliases: ["RecYd_Mean", "RecYds_Mean", "RecYd", "RecYds", "Rec Yds"], sourceOnly: "proj" },
    { label: "Rec TD", aliases: ["RecTD_Mean", "RecTD", "Rec TD"], sourceOnly: "proj" },
  ],
  DST: [
    ...BASE_PLAYER_COLS,
    { label: "O/U", aliases: CONTEXT_ALIASES.total, sourceOnly: "ctx" },
    { label: "Imp Total", aliases: CONTEXT_ALIASES.imp, sourceOnly: "ctx" },
    ...DKFD_COLS,
    { label: "Sacks", aliases: ["DST_Sacks_Mean", "DST_Sacks", "Sacks", "Sack"], sourceOnly: "proj" },
    { label: "INT", aliases: ["DST_INT_Mean", "DST_INT", "Int", "INT"], sourceOnly: "proj" },
    { label: "Fum Rec", aliases: ["DST_FumRec_Mean", "DST_FumRec", "Fum", "Fumbles", "Fum Rec"], sourceOnly: "proj" },
    { label: "Def TD", aliases: ["DST_DefTD_Mean", "DST_DefTD", "Def TD", "DefTD"], sourceOnly: "proj" },
    { label: "Pts Allowed", aliases: ["DST_PointsAllowed_Mean", "DST_PointsAllowed", "Points Allowed", "Pts Allowed"], sourceOnly: "proj" },
  ],
  K: [
    ...BASE_PLAYER_COLS,
    { label: "O/U", aliases: CONTEXT_ALIASES.total, sourceOnly: "ctx" },
    { label: "Imp Total", aliases: CONTEXT_ALIASES.imp, sourceOnly: "ctx" },
    ...DKFD_COLS,
    { label: "XP Att", aliases: ["XP_Att_Mean", "XP_Att", "XP Att"], sourceOnly: "proj" },
    { label: "XP Made", aliases: ["XP_Made_Mean", "XP_Made", "XP Made", "XPM"], sourceOnly: "proj" },
    { label: "FG Att", aliases: ["FG_Att_Mean", "FG_Att", "FG Att", "FGA"], sourceOnly: "proj" },
    { label: "FG Made", aliases: ["FG_Made_Mean", "FG_Made", "FG Made", "FGM"], sourceOnly: "proj" },
    { label: "FG 40-49", aliases: ["FG40_49_Made_Mean", "FG40_49_Made"], sourceOnly: "proj" },
    { label: "FG 50+", aliases: ["FG50p_Made_Mean", "FG50p_Made"], sourceOnly: "proj" },
  ],
};
const TEAM_PROJECTION_COLS = [
  { label: "Team", aliases: ["Team", "team", "Tm"] },
  { label: "OPP", aliases: ["Opp", "OPP", "Opponent"] },
  { label: "DK Mean", aliases: ["DK_Mean", "DK Mean", "DKProj", "DK Pts"], sourceOnly: "proj" },
  { label: "FD Mean", aliases: ["FD_Mean", "FD Mean", "FDProj", "FD Pts"], sourceOnly: "proj" },
  { label: "Points", aliases: ["Points_Mean", "Points", "TeamPoints_Mean", "Team Points"] },
  { label: "Plays", aliases: ["Plays_Mean", "Plays"] },
  { label: "Drives", aliases: ["Drives_Mean", "Drives"] },
  { label: "Pass Att", aliases: ["PassAtt_Mean", "PassAtt", "TeamPassAtt_Mean"], sourceOnly: "proj" },
  { label: "Rush Att", aliases: ["RushAtt_Mean", "RushAtt", "TeamRushAtt_Mean"], sourceOnly: "proj" },
  { label: "Pass Yds", aliases: ["PassYd_Mean", "PassYds_Mean", "PassYd", "PassYds"], sourceOnly: "proj" },
  { label: "Rush Yds", aliases: ["RushYd_Mean", "RushYds_Mean", "RushYd", "RushYds"], sourceOnly: "proj" },
];
function colKeyFromMergedRows(rows, c) {
  const projCols = unionKeys(rows.map((r) => r.proj));
  const ctxCols = unionKeys(rows.map((r) => r.ctx));
  if (keynorm(c.label) === keynorm("Player")) {
    return { ...c, key: "__player", source: "computed" };
  }
  // CRITICAL: projected stat columns must NEVER fall through to positional-data keys.
  // Context files are only for salary / opponent / time / vegas-style metadata.
  if (c.sourceOnly === "proj") {
    const projKey = resolveKey(projCols, c.aliases);
    return { ...c, key: projKey, source: projKey ? "proj" : "" };
  }
  if (c.sourceOnly === "ctx") {
    const ctxKey = resolveKey(ctxCols, c.aliases);
    return { ...c, key: ctxKey, source: ctxKey ? "ctx" : "" };
  }
  const projKey = resolveKey(projCols, c.aliases);
  const ctxKey = resolveKey(ctxCols, c.aliases);
  return { ...c, key: projKey || ctxKey, source: projKey ? "proj" : ctxKey ? "ctx" : "" };
}
function valueForCol(merged, c, pos) {
  if (c.key === "__player") return getMergedPlayer(merged, pos);
  if (!c.key) return "";
  const src = c.source === "proj" ? merged.proj : merged.ctx;
  return src?.[c.key] ?? "";
}
function importantValue(merged, aliases) {
  const raw = getFirstMerged(merged, aliases);
  const n = parseNumericLike(raw);
  return n == null ? null : n;
}
function importantProjValue(merged, aliases) {
  const raw = getFirst(merged?.proj, aliases);
  const n = parseNumericLike(raw);
  return n == null ? null : n;
}
function makeContextIndex(contextByPos) {
  const strict = new Map();
  const loose = new Map();
  for (const pos of POS_ORDER) {
    for (const r of contextByPos[pos] || []) {
      const id = rowIdentity(r, pos);
      if (!strict.has(id)) strict.set(id, r);
      const lid = looseIdentity(r);
      if (!loose.has(lid)) loose.set(lid, r);
    }
  }
  return { strict, loose };
}
function inferPosFromProjection(row) {
  const slot = getSlot(row);
  if (POS_ORDER.includes(slot)) return slot;
  const raw = norm(getFirst(row, ["Position", "Pos", "pos", "position"])).toUpperCase();
  if (POS_ORDER.includes(raw)) return raw;
  const player = getPlayer(row);
  const team = getTeam(row);
  if (player && team && player.toUpperCase().includes("DST")) return "DST";
  return "";
}
function mergeProjectionRows(playerProjRows, contextByPos) {
  const idx = makeContextIndex(contextByPos);
  const out = [];
  for (const p of playerProjRows || []) {
    const pos = inferPosFromProjection(p);
    if (!POS_ORDER.includes(pos)) continue;
    const strictId = rowIdentity(p, pos);
    const looseId = looseIdentity(p);
    const ctx = idx.strict.get(strictId) || idx.loose.get(looseId) || null;
    out.push({ pos, proj: p, ctx });
  }
  return out;
}
function exportTeamCSV(team, grouped, colsByPos) {
  const esc = (s) => /[",\r\n]/.test(String(s ?? "")) ? `"${String(s ?? "").replace(/"/g, '""')}"` : String(s ?? "");
  const lines = [];
  for (const pos of POS_ORDER) {
    const rows = grouped[pos] || [];
    if (!rows.length) continue;
    const cols = colsByPos[pos] || [];
    lines.push(pos);
    lines.push(cols.map((c) => esc(c.label)).join(","));
    for (const r of rows) lines.push(cols.map((c) => esc(fmtCellValue(c.label, valueForCol(r, c, pos)))).join(","));
    lines.push("");
  }
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `nfl_${team.toLowerCase()}_projection_dashboard.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
function heatDirection(label, pos = "") {
  const l = String(label || "").trim();
  if (!l) return null;
  // Do not heat identity/context columns that are not value/ranking metrics.
  if (/^(player|team|opp|time)$/i.test(l)) return null;
  // Lower is better for these. QB INT = bad. DST INT = good.
  if (/salary|\bsal\b|pts allowed/i.test(l)) return "lower";
  if (/^int$/i.test(l) && String(pos).toUpperCase() !== "DST") return "lower";
  // Higher is better / more useful for the dashboard context.
  if (
    /dk mean|fd mean|floor|ceiling|yards|yds|td|targets|target share|receptions|rec|points|plays|drives|imp total|o\/u|ypc|ypa|comp|msair|adot|routes|sacks|fum|fg|xp|pass att|rush att/i.test(l) ||
    (/^int$/i.test(l) && String(pos).toUpperCase() === "DST")
  ) {
    return "higher";
  }
  return null;
}
function heatTextColor(min, max, raw, dir, theme) {
  const n = parseNumericLike(raw);
  if (n == null || min == null || max == null || min === max || !dir) return null;
  let t = (n - min) / (max - min);
  t = Math.max(0, Math.min(1, t));
  if (dir === "lower") t = 1 - t;
  // Red -> yellow -> green. Same idea as your other table heatmaps, but now based on
  // the full slate / all teams instead of only the selected team rows.
  const isDark = theme === "dark";
  let hue;
  let sat;
  let light;
  if (t < 0.5) {
    const u = t / 0.5;
    hue = 0 + u * 55;
    sat = isDark ? 84 : 86;
    light = isDark ? 62 + u * 8 : 43 + u * 3;
  } else {
    const u = (t - 0.5) / 0.5;
    hue = 55 + u * 75;
    sat = isDark ? 78 : 82;
    light = isDark ? 70 - u * 8 : 44 - u * 8;
  }
  return `hsl(${hue}, ${sat}%, ${light}%)`;
}
function buildHeatStatsForMergedRows(rows, cols, pos) {
  const stats = {};
  for (const c of cols || []) {
    const dir = heatDirection(c.label, pos);
    if (!dir || !c.key || c.key === "__player") continue;
    let min = Infinity;
    let max = -Infinity;
    for (const row of rows || []) {
      const raw = valueForCol(row, c, pos);
      const n = parseNumericLike(raw);
      if (n == null) continue;
      if (n < min) min = n;
      if (n > max) max = n;
    }
    if (min !== Infinity && max !== -Infinity && min !== max) {
      stats[c.label] = { min, max, dir };
    }
  }
  return stats;
}
function buildHeatStatsForRawRows(rows, cols, pos = "TEAM") {
  const stats = {};
  for (const c of cols || []) {
    const dir = heatDirection(c.label, pos);
    if (!dir || !c.key) continue;
    let min = Infinity;
    let max = -Infinity;
    for (const row of rows || []) {
      const n = parseNumericLike(row?.[c.key]);
      if (n == null) continue;
      if (n < min) min = n;
      if (n > max) max = n;
    }
    if (min !== Infinity && max !== -Infinity && min !== max) {
      stats[c.label] = { min, max, dir };
    }
  }
  return stats;
}
function statColor(label, raw, theme, heatStats) {
  const st = heatStats?.[label];
  if (!st) return null;
  return heatTextColor(st.min, st.max, raw, st.dir, theme);
}
export default function NflTeamDataDashboard() {
  const playerProj = useJson(PROJECTION_SOURCES.player);
  const teamProj = useJson(PROJECTION_SOURCES.team, true);
  useJson(PROJECTION_SOURCES.game, true); // keep warm / optional, not rendered directly yet
  const qbCtx = useJson(CONTEXT_SOURCES.QB, true);
  const rbCtx = useJson(CONTEXT_SOURCES.RB, true);
  const wrCtx = useJson(CONTEXT_SOURCES.WR, true);
  const teCtx = useJson(CONTEXT_SOURCES.TE, true);
  const dstCtx = useJson(CONTEXT_SOURCES.DST, true);
  const kCtx = useJson(CONTEXT_SOURCES.K, true);
  const meta = useMeta(META_URL);
  const [theme, setTheme] = useState("dark");
  const isDark = theme === "dark";
  const T = isDark ? DARK_THEME : LIGHT_THEME;
  const contextByPos = useMemo(() => ({
    QB: qbCtx.data,
    RB: rbCtx.data,
    WR: wrCtx.data,
    TE: teCtx.data,
    DST: dstCtx.data,
    K: kCtx.data,
  }), [qbCtx.data, rbCtx.data, wrCtx.data, teCtx.data, dstCtx.data, kCtx.data]);
  const mergedRows = useMemo(() => mergeProjectionRows(playerProj.data, contextByPos), [playerProj.data, contextByPos]);
  const teams = useMemo(() => {
    const s = new Set();
    for (const m of mergedRows) {
      const t = getTeam(m.proj) || getTeam(m.ctx);
      if (t) s.add(t);
    }
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [mergedRows]);
  const initialTeam = useMemo(() => {
    const qp = new URLSearchParams(window.location.search);
    return fixAbbr(qp.get("team") || localStorage.getItem("nflTeamDashboardTeam") || "ARI");
  }, []);
  const [team, setTeam] = useState(initialTeam);
  const [q, setQ] = useState("");
  const compact = true;
  useEffect(() => {
    if (!teams.length) return;
    if (!teams.includes(team)) setTeam(teams[0]);
  }, [teams, team]);
  useEffect(() => {
    if (!team) return;
    localStorage.setItem("nflTeamDashboardTeam", team);
    const qp = new URLSearchParams(window.location.search);
    qp.set("team", team);
    window.history.replaceState({}, "", `${window.location.pathname}?${qp.toString()}`);
  }, [team]);
  const allRowsByPos = useMemo(() => {
    const out = Object.fromEntries(POS_ORDER.map((p) => [p, []]));
    for (const m of mergedRows) {
      if (POS_ORDER.includes(m.pos)) out[m.pos].push(m);
    }
    return out;
  }, [mergedRows]);
  const grouped = useMemo(() => {
    const out = Object.fromEntries(POS_ORDER.map((p) => [p, []]));
    const needle = lower(q);
    for (const m of mergedRows) {
      const rowTeam = getTeam(m.proj) || getTeam(m.ctx);
      if (rowTeam !== team) continue;
      const pos = m.pos;
      if (!POS_ORDER.includes(pos)) continue;
      if (needle) {
        const player = getMergedPlayer(m, pos);
        const opp = getFirstMerged(m, CONTEXT_ALIASES.opp);
        if (!lower(`${player} ${opp}`).includes(needle)) continue;
      }
      out[pos].push(m);
    }
    for (const pos of POS_ORDER) {
      out[pos].sort((a, b) => {
        const aPts = importantProjValue(a, ["DK_Mean", "DK Mean", "DK Proj", "DKPts", "DK Pts", "FD_Mean"]);
        const bPts = importantProjValue(b, ["DK_Mean", "DK Mean", "DK Proj", "DKPts", "DK Pts", "FD_Mean"]);
        if (aPts != null || bPts != null) return (bPts ?? -Infinity) - (aPts ?? -Infinity);
        return getMergedPlayer(a, pos).localeCompare(getMergedPlayer(b, pos));
      });
    }
    return out;
  }, [mergedRows, team, q]);
  const colsByPos = useMemo(() => {
    const out = {};
    for (const pos of POS_ORDER) {
      // Resolve keys from the full slate at that position, not only the selected team.
      // This also gives the heatmap a stable all-teams baseline.
      const rows = allRowsByPos[pos] || [];
      out[pos] = (COLUMN_SETS[pos] || COLUMN_SETS.QB)
        .map((c) => colKeyFromMergedRows(rows, c))
        .filter((c) => c.key || keynorm(c.label) === keynorm("Player"));
    }
    return out;
  }, [allRowsByPos]);
  const heatStatsByPos = useMemo(() => {
    const out = {};
    for (const pos of POS_ORDER) {
      out[pos] = buildHeatStatsForMergedRows(allRowsByPos[pos] || [], colsByPos[pos] || [], pos);
    }
    return out;
  }, [allRowsByPos, colsByPos]);
  const allTeamProjectionRows = useMemo(() => teamProj.data || [], [teamProj.data]);
  const teamProjectionRows = useMemo(() => allTeamProjectionRows.filter((r) => getTeam(r) === team), [allTeamProjectionRows, team]);
  const teamProjectionCols = useMemo(() => {
    const raw = unionKeys(allTeamProjectionRows);
    return TEAM_PROJECTION_COLS.map((c) => ({ ...c, key: resolveKey(raw, c.aliases) })).filter((c) => c.key);
  }, [allTeamProjectionRows]);
  const teamHeatStats = useMemo(() => buildHeatStatsForRawRows(allTeamProjectionRows, teamProjectionCols, "TEAM"), [allTeamProjectionRows, teamProjectionCols]);
  const totals = useMemo(() => {
    let players = 0;
    let dk = 0;
    let fd = 0;
    for (const pos of POS_ORDER) {
      for (const r of grouped[pos] || []) {
        players += 1;
        dk += importantProjValue(r, ["DK_Mean", "DK Mean", "DK Proj", "dk_proj", "DKPts", "DK Pts"]) || 0;
        fd += importantProjValue(r, ["FD_Mean", "FD Mean", "FD Proj", "fd_proj", "FDPts", "FD Pts"]) || 0;
      }
    }
    return { players, dk, fd };
  }, [grouped]);
  const updatedStamp = useMemo(() => {
    const raw = meta?.last_updated || meta?.updated_iso || meta?.updated_at;
    if (!raw) return null;
    const d = new Date(raw);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleString(undefined, { month: "numeric", day: "numeric", hour: "numeric", minute: "2-digit" });
  }, [meta]);
  const loading = playerProj.loading;
  const err = playerProj.err;
  const teamMeta = TEAM_META[team] || { name: team, primary: "#0f172a", secondary: "#38bdf8" };
  return (
    <div className={T.page}>
      <div className="mx-auto w-full px-4 md:px-6 py-5">
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border bg-white/95 p-1" style={{ borderColor: teamMeta.secondary }}>
                <img src={`${LOGO_BASE}/${team}.${LOGO_EXT}`} alt="" className="max-h-8 max-w-8 object-contain" onError={(e) => (e.currentTarget.style.display = "none")} />
              </div>
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">NFL Team Projection Dashboard</div>
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <h1 className="text-xl font-bold tracking-tight">{teamMeta.name}</h1>
                  <span className={"text-xs font-normal italic " + (isDark ? "text-slate-100" : "text-slate-700")}>Updated: {updatedStamp || "—"}</span>
                </div>
                <div className={`mt-1 flex flex-wrap gap-3 text-xs ${T.subtext}`}>
                  <span>Player DK sum: {totals.dk.toFixed(1)}</span>
                  <span>Player FD sum: {totals.fd.toFixed(1)}</span>
                </div>
              </div>
            </div>
            <div className="ml-auto shrink-0">
              <button type="button" onClick={() => setTheme(isDark ? "light" : "dark")} className="h-8 rounded-md bg-sky-500 px-3 text-xs font-bold text-slate-950 hover:bg-sky-400">
                {isDark ? "Light" : "Dark"}
              </button>
            </div>
          </div>
        </div>
            <div className={`mt-3 flex flex-wrap items-center gap-2 rounded-2xl ${T.card} p-3`}>
              <select value={team} onChange={(e) => setTeam(fixAbbr(e.target.value))} className={`h-8 min-w-0 rounded-md border px-3 text-xs font-semibold outline-none focus:ring-2 focus:ring-sky-500 ${T.input}`}>
                {teams.map((t) => (
                  <option key={t} value={t}>{TEAM_META[t]?.name || t}</option>
                ))}
              </select>
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search player / opp…" className={`h-8 w-full sm:w-64 sm:ml-auto rounded-md border px-3 text-xs outline-none focus:ring-2 focus:ring-sky-500 ${T.input}`} />
              <button type="button" onClick={() => exportTeamCSV(team, grouped, colsByPos)} className="h-8 rounded-md bg-emerald-500 px-3 text-xs font-bold text-slate-950 hover:bg-emerald-400">
                Export Team CSV
              </button>

            </div>
        {loading && <div className={`mt-4 rounded-2xl p-4 text-sm ${T.card}`}>Loading NFL player projections…</div>}
        {err && <div className="mt-4 rounded-2xl border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-300">Failed to load projection file: {err}</div>}
        <TeamProjectionSection rows={teamProjectionRows} cols={teamProjectionCols} heatStats={teamHeatStats} team={team} teamMeta={teamMeta} theme={theme} T={T} compact={compact} />
        <div className="mt-4 grid grid-cols-1 gap-4">
          {POS_ORDER.map((pos) => (
            <PositionSection key={pos} pos={pos} rows={grouped[pos] || []} cols={colsByPos[pos] || []} heatStats={heatStatsByPos[pos] || {}} team={team} teamMeta={teamMeta} theme={theme} T={T} compact={compact} />
          ))}
        </div>
      </div>
    </div>
  );
}
function TeamProjectionSection({ rows, cols, heatStats, team, teamMeta, theme, T, compact }) {
  const isDark = theme === "dark";
  const pad = compact ? "px-2 py-1" : "px-3 py-2";
  const text = compact ? "text-[11.5px]" : "text-xs md:text-[13px]";
  if (!rows.length || !cols.length) return null;
  return (
    <section className={`mt-4 overflow-hidden rounded-2xl ${T.card}`}>
      <div className="flex w-full items-center justify-between border-b px-4 py-3 text-left" style={{ borderColor: isDark ? "rgba(30,41,59,.9)" : "rgba(226,232,240,.95)" }}>
        <div className="flex items-center gap-3">
          <div className="text-xs font-bold text-sky-400">TEAM</div>
          <div>
            <div className="text-sm font-bold">Team Projection</div>
          </div>
        </div>
      </div>
      <div className="max-h-[72vh] overflow-auto">
        <table className={`w-max min-w-full border-separate ${text}`} style={{ borderSpacing: 0 }}>
          <thead><tr className={`${T.headerBg} sticky top-0 z-20`}>{cols.map((c) => <th key={c.label} className={`${pad} whitespace-nowrap border-r text-center font-bold ${T.border} ${T.text}`}>{c.label}</th>)}</tr></thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className={i % 2 ? T.zebraEven : T.zebraOdd}>
                {cols.map((c) => {
                  const raw = row?.[c.key];
                  const isOpp = keynorm(c.label) === keynorm("OPP");
                  const isTeam = keynorm(c.label) === keynorm("Team");
                  const val = isOpp || isTeam ? <TeamWithLogo code={raw} className="h-4 w-4" /> : fmtCellValue(c.label, raw);
                  const color = statColor(c.label, raw, theme, heatStats);
                  return <td key={c.label} className={`${pad} whitespace-nowrap border-r text-center ${T.border} ${T.text}`} style={color ? { color, fontWeight: 700 } : undefined}>{val}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
function PositionSection({ pos, rows, cols, heatStats, team, teamMeta, theme, T, compact }) {
  const [open, setOpen] = useState(true);
  const isDark = theme === "dark";
  const pad = compact ? "px-2 py-1" : "px-3 py-2";
  const text = compact ? "text-[11.5px]" : "text-xs md:text-[13px]";
  if (rows.length === 0) return null;
  return (
    <section className={`overflow-hidden rounded-2xl ${T.card}`}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex w-full items-center justify-between border-b px-4 py-3 text-left" style={{ borderColor: isDark ? "rgba(30,41,59,.9)" : "rgba(226,232,240,.95)" }}>
        <div className="flex items-center gap-3">
          <div className="text-xs font-bold text-sky-400">{pos}</div>
          <div>
            <div className="text-sm font-bold">{team} {pos}</div>
          </div>
        </div>
        <span className={`text-xs font-bold ${T.subtext}`}>{open ? "Hide" : "Show"}</span>
      </button>
      {open && (
        <div className="max-h-[72vh] overflow-auto">
          <table className={`w-max min-w-full border-separate ${text}`} style={{ borderSpacing: 0 }}>
            <thead>
              <tr className={`${T.headerBg} sticky top-0 z-20`}>
                {cols.map((c, idx) => {
                  const isPlayer = keynorm(c.label) === keynorm("Player");
                  const srcTag = c.source === "proj" ? "Projection" : c.source === "ctx" ? "Context" : "Computed";
                  return (
                    <th key={`${pos}-${c.label}-${idx}`} className={`${pad} whitespace-nowrap border-r text-center font-bold ${T.border} ${T.text} ${isPlayer ? `${T.headerBg} sticky left-0 z-30 min-w-[16rem]` : ""}`} title={`${srcTag} key: ${c.key || "—"}`}>
                      {c.label}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rIdx) => (
                <tr key={`${pos}-${rIdx}`} className={`${rIdx % 2 ? T.zebraEven : T.zebraOdd} hover:bg-sky-500/10`}>
                  {cols.map((c, cIdx) => {
                    const raw = valueForCol(row, c, pos);
                    const isPlayer = keynorm(c.label) === keynorm("Player");
                    const isOpp = keynorm(c.label) === keynorm("OPP");
                    const val = isOpp ? <TeamWithLogo code={raw} className="h-4 w-4" /> : fmtCellValue(c.label, raw);
                    const color = statColor(c.label, raw, theme, heatStats);
                    if (isPlayer) {
                      return (
                        <td key={`${pos}-${rIdx}-${cIdx}`} className={`${pad} sticky left-0 z-10 min-w-[16rem] whitespace-nowrap border-r font-semibold ${T.border} ${T.text} ${rIdx % 2 ? T.zebraEven : T.zebraOdd}`}>
                          <span className="inline-flex items-center gap-2">
                            <TeamWithLogo code={team} className="h-4 w-4" />
                            <span>{getMergedPlayer(row, pos) || fmtCellValue(c.label, raw)}</span>
                          </span>
                        </td>
                      );
                    }
                    return (
                      <td key={`${pos}-${rIdx}-${cIdx}`} className={`${pad} whitespace-nowrap border-r text-center ${T.border} ${T.text}`} style={color ? { color, fontWeight: 700 } : undefined}>
                        {val}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
